import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from '../services/api.service';
import { OrderLinkingDialogComponent } from './order-linking-dialog.component';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { toUtcIsoDate } from '../utils/date-utils';
import { VendorComponent } from './vendor.component';

@Component({
  standalone: true,
  selector: 'app-salesorder',
  templateUrl: './salesorder.component.html',
  styleUrl: './salesorder.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatTableModule,
    MatDialogModule,
    RouterLink
  ]
})
export class SalesOrderComponent implements OnInit {
  salesOrderForm: FormGroup;
  unitTypes: string[] = [];
  orderStatuses: any[] = [];
  paymentTermsList: any[] = [];
  customerContacts: any[] = [];
  customerAddresses: any[] = [];
  customers: any[] = [];
  purchaseOrderProductsList: any[] = [];
  lotNumsList: any[] = [];
  filteredCustomers!: Observable<any[]>;
  filteredPurchaseOrderProductsList: Observable<any[]>[] = [];
  filteredLotNumsList: Observable<any[]>[] = [];
  salesOrderId: number | null = null;
  isEditMode: boolean = false;
  errorMessage: string = '';
  pendingInventoryError: boolean = false;
  purchaseOrders: any[] = [];
  purchaseOrderColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  salesInvoices: any[] = [];
  salesInvoiceColumns = ['invoiceNumber', 'date', 'status', 'totalAmount', 'siActions'];
  proformas: any[] = [];
  proformaColumns = ['proformaNumber', 'date', 'status', 'totalAmount', 'pfActions'];

  constructor(
    private fb: FormBuilder, 
    private router: Router, 
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    this.salesOrderForm = this.fb.group({
      // Order base class properties
      number: ['', Validators.required],
      date: [new Date(), Validators.required],
      status: ['Draft'],
      shipper: ['FedEx'],
      shipAccountNum: [''],
      shipTerms: [''],
      paymentTerms: [''],
      desiredShipDate: [null],
      shipAttentionTo: [''],
      salesPerson: [''],
      taxAmount: [0, [Validators.min(0)]],
      shipFee: [0, [Validators.min(0)]],
      handlingFee: [0, [Validators.min(0)]],
      discountAmount: [0, [Validators.min(0)]],
      
      // SalesOrder specific properties
      customerId: [null, Validators.required],
      customerSearch: [''],
      customerPO: [''],
      billToAddressId: [null],
      shipToAddressId: [null],
      customerContactId: [null],
      
      // Products array
      products: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadUnitTypes();
    this.loadOrderStatuses();
    this.loadPaymentTerms();
    this.loadCustomers();
    this.loadPurchaseOrderProducts();
    this.loadLotNums();
    
    // Check if there's an id parameter in the route
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && !isNaN(+id)) {
        this.salesOrderId = +id;
        this.isEditMode = false;
        this.loadSalesOrder(this.salesOrderId);
        this.loadRelatedPurchaseOrders(this.salesOrderId);
        this.loadRelatedSalesInvoices(this.salesOrderId);
        this.loadRelatedProformas(this.salesOrderId);
      } else {
        this.isEditMode = true;
        this.loadLatestOrderNumber();
      }
    });
    
    // Setup customer search autocomplete
    this.filteredCustomers = this.salesOrderForm.get('customerSearch')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCustomers(value || ''))
    );
    
    // Watch for customerId changes to load related contacts and addresses
    this.salesOrderForm.get('customerId')?.valueChanges.subscribe(customerId => {
      if (customerId) {
        // Clear previously selected contact and address values
        this.salesOrderForm.patchValue({
          customerContactId: null,
          billToAddressId: null,
          shipToAddressId: null
        }, { emitEvent: false });
        
        // Load new contacts and addresses for the selected customer
        this.loadCustomerContacts(customerId);
        this.loadCustomerAddresses(customerId);
      } else {
        this.customerContacts = [];
        this.customerAddresses = [];
        this.salesOrderForm.patchValue({
          customerContactId: null,
          billToAddressId: null,
          shipToAddressId: null
        }, { emitEvent: false });
      }
    });

    // Pre-populate from navigation state — either a full copy of another Sales Order,
    // or a narrower prefill generated from a Purchase Order.
    const prefill = history.state?.prefill;
    if (history.state?.copySalesOrder && prefill) {
      while (this.products.length) this.products.removeAt(0);
      this.populateSalesOrderForm(prefill, true);
      return;
    }
    if (prefill?.poId > 0) 
    {
      if (!this.purchaseOrders.find(po => po.id === prefill.poId)) {
        this.purchaseOrders.push({ id: prefill.poId, number: `${prefill.poNumber}`, date: prefill.poDate, status: prefill.poStatus, totalAmount: prefill.poTotal });
      }
    }
    if (prefill) {
      this.salesOrderForm.patchValue({
        salesPerson: prefill.salesPerson || ''
      });
    }
    if (prefill?.products?.length) {
      prefill.products.forEach((p: any) => {
        console.log('Prefill product:', p);
        const type = p.purchaseOrderProductId ? 'purchaseOrderProduct' : (p.lotNumId ? 'lotNum' : 'purchaseOrderProduct');
        this.products.push(this.fb.group({
          lineItemType:               [type],
          purchaseOrderProductId:     [p.purchaseOrderProductId || ''],
          purchaseOrderProductSearch: [''],
          lotNumId:                   [p.lotNumId || ''],
          lotNumSearch:               [''],
          productId:                  [p.productId || ''],
          productName:                [''],
          catalogNum:                 [p.catalogNum || ''],
          //lotNumber:                 [p.lotNumber || ''],
          vendorCatalogNum:           [p.vendorCatalogNum || ''],
          vendorLotNum:               [p.vendorLotNum || ''],
          quantityAvailable:          [p.quantityAvailable ?? 0],
          unitPrice:                  [0, [Validators.required, Validators.min(0)]],
          unitType:                   [p.unitType || ''],
          quantity:                   [p.quantity || 1, [Validators.required, Validators.min(0.01)]],
          lineNumber:                 [this.products.length + 1],
          description:                [p.description || ''],
          notes:                      [p.notes || ''],
          desiredPackaging:           [p.desiredPackaging || ''],
          poNumber:                   [p.poNumber || ''],
          costPerUnit:                [p.costPerUnit || 0]
        }));
        const productIndex = this.products.length - 1;
        if (type === 'lotNum') {
          this.setupLotNumAutocomplete(productIndex);
          // if (p.lotNumId > 0) {
          //   this.apiService.get<any>(`product/lotnum/${p.lotNumId}`).subscribe({
          //     next: (data) => {
          //       if (data) {
          //         this.products.at(productIndex).patchValue({
          //           lotNumSearch: data
          //         }, { emitEvent: false });
          //       }
          //     },
          //     error: (err) => console.error('Error loading lot number:', err)
          //   });
          // }
        } else {
          this.setupPurchaseOrderProductAutocomplete(productIndex);
        }
      });
    }
  }

  private showError(message: string, err?: any): void {
    let detail: string | undefined;
    if (err?.error) {
      // err.error is the parsed response body
      if (typeof err.error === 'string') {
        detail = err.error;
      } else {
        detail = err.error.detail || err.error.message || err.error.title || err.error.error;
      }
    }
    // Fall back to HTTP status text only (not err.message which includes the full URL)
    if (!detail && err?.statusText && err.statusText !== 'OK') {
      detail = `(${err.status}: ${err.statusText})`;
    }
    this.errorMessage = detail ? `${message} ${detail}` : message;
    this.cdr.detectChanges();
  }

  enterEditMode(): void {
    this.isEditMode = true;
    this.setSelectsDisabled(false);
    const formValue = this.salesOrderForm.value;
    if (formValue.customerId > 0) {
      this.loadCustomerContacts(formValue.customerId);
      this.loadCustomerAddresses(formValue.customerId);
    }
  }

  private setSelectsDisabled(disabled: boolean): void {
    const controls = ['status', 'customerContactId', 'billToAddressId', 'shipToAddressId', 'paymentTerms'];
    controls.forEach(ctrl => {
      const control = this.salesOrderForm.get(ctrl);
      disabled ? control?.disable({ emitEvent: false }) : control?.enable({ emitEvent: false });
    });
    this.products.controls.forEach(product => {
      const unitType = product.get('unitType');
      disabled ? unitType?.disable({ emitEvent: false }) : unitType?.enable({ emitEvent: false });
    });
  }

  loadSalesOrder(id: number): void {
    while (this.products.length) this.products.removeAt(0);
    this.apiService.get<any>(`salesorder/${id}`).subscribe({
      next: (data) => this.populateSalesOrderForm(data, false),
      error: (error) => {
        console.error('Error loading sales order:', error);
        this.showError('Failed to load sales order. Please try again.', error);
      }
    });
  }

  // Populates the sales order form and products array from API data.
  // When isCopy is true, the order number/status/ship date and line item ids are reset so
  // the result is saved as a brand-new sales order rather than an update.
  private populateSalesOrderForm(data: any, isCopy: boolean): void {
    // Handle both camelCase and PascalCase from API
    const salesOrder = {
      number: isCopy ? '' : (data.number || data.Number || ''),
      date: isCopy ? new Date() : ((data.date || data.Date) ? new Date(data.date || data.Date) : new Date()),
      status: isCopy ? 'Draft' : (data.status || data.Status || 'Draft'),
      shipper: data.shipper || data.Shipper || '',
      shipAccountNum: data.shipAccountNum || data.ShipAccountNum || '',
      shipTerms: data.shipTerms || data.ShipTerms || '',
      paymentTerms: data.paymentTerms || data.PaymentTerms || '',
      desiredShipDate: isCopy ? null : ((data.desiredShipDate || data.DesiredShipDate) ? new Date(data.desiredShipDate || data.DesiredShipDate) : null),
      shipAttentionTo: data.shipAttentionTo || data.ShipAttentionTo || '',
      salesPerson: data.salesPerson || data.SalesPerson || '',
      taxAmount: data.taxAmount || data.TaxAmount || 0,
      shipFee: data.shippingAmount || data.ShippingAmount || 0,
      handlingFee: data.handlingFee || data.HandlingFee || 0,
      discountAmount: data.discountAmount || data.DiscountAmount || 0,
      customerId: data.customerId || data.CustomerId || null,
      customerPO: data.customerPO || data.CustomerPO || '',
      billToAddressId: data.billToAddressId || data.BillToAddressId || null,
      shipToAddressId: data.shipToAddressId || data.ShipToAddressId || null,
      customerContactId: data.customerContactId || data.CustomerContactId || null
    };

    // Check if API response includes nested customer contact and address objects
    const customerContact = data.customerContact || data.CustomerContact;
    const billToAddress = data.billToAddress || data.BillToAddress;
    const shipToAddress = data.shipToAddress || data.ShipToAddress;
    const customerObj = data.customer || data.Customer;

    // If nested objects exist, populate the arrays directly
    if (customerContact || billToAddress || shipToAddress) {
      this.customerContacts = [];
      this.customerAddresses = [];

      if (customerContact) {
        this.customerContacts.push(customerContact);
      }
      if (billToAddress) {
        this.customerAddresses.push(billToAddress);
      }
      if (shipToAddress && shipToAddress !== billToAddress) {
        this.customerAddresses.push(shipToAddress);
      }
    }

    // Patch form values without triggering valueChanges for customerId
    this.salesOrderForm.patchValue(salesOrder, { emitEvent: false });

    // Set the customer for the search field display
    if (salesOrder.customerId) {
      // First try to use nested customer object from API
      if (customerObj) {
        this.salesOrderForm.patchValue({ customerSearch: customerObj }, { emitEvent: false });
      } else {
        // Otherwise, try to find from loaded customers array or fetch it
        const customer = this.customers.find(c => (c.id || c.Id) === salesOrder.customerId);
        if (customer) {
          this.salesOrderForm.patchValue({ customerSearch: customer }, { emitEvent: false });
        } else {
          // Load the specific customer if not found in the list
          this.apiService.get<any>(`customer/${salesOrder.customerId}`).subscribe({
            next: (customerData) => {
              this.salesOrderForm.patchValue({ customerSearch: customerData }, { emitEvent: false });
            },
            error: (err) => console.error('Error loading customer:', err)
          });
        }
      }
    }

    // If nested data wasn't provided, trigger the load methods manually
    if (salesOrder.customerId && (!customerContact && !billToAddress && !shipToAddress)) {
      this.loadCustomerContacts(salesOrder.customerId);
      this.loadCustomerAddresses(salesOrder.customerId);
    }

    // Load products if available
    const productsData = data.products || data.Products || [];
    productsData.forEach((product: any) => {
      const type = product.purchaseOrderProductId || product.PurchaseOrderProductId
        ? 'purchaseOrderProduct'
        : (product.lotNumId || product.LotNumId ? 'lotNum' : 'purchaseOrderProduct');
      // Extract display data from navigation properties returned by the API
      const lotNumNav = product.lotNum || product.LotNum;
      const popNav    = product.purchaseOrderProduct || product.PurchaseOrderProduct;
      const navSource = lotNumNav?.vendorProduct?.product || lotNumNav?.product
                      || popNav?.vendorProduct?.product  || popNav?.product;
      // Compute quantity available directly from the nested navigation data returned with
      // this sales order, since the already-selected item may no longer appear in the
      // separate pending-inventory / lot-nums lists used for autocomplete searches.
      const initialQuantityAvailable = type === 'lotNum'
        ? (lotNumNav?.quantityAvailable ?? lotNumNav?.QuantityAvailable ?? null)
        : (popNav
            ? (popNav?.quantityAvailable || popNav?.QuantityAvailable || 0)
            : null);
      this.products.push(this.fb.group({
        lineItemType:               [type],
        id:                         [isCopy ? null : (product.id || product.Id || null)],
        purchaseOrderProductId:     [product.purchaseOrderProductId || product.PurchaseOrderProductId || ''],
        purchaseOrderProductSearch: [type === 'purchaseOrderProduct' && popNav ? popNav : ''],
        lotNumId:                   [lotNumNav?.id || lotNumNav?.Id || product.purchaseOrderProduct?.lotNumId || product.PurchaseOrderProduct?.LotNumId || ''],
        lotNumSearch:               [type === 'lotNum' && lotNumNav ? lotNumNav : ''],
        productId:                  [''],
        productName:                [navSource?.name || navSource?.Name || ''],
        catalogNum:                 [navSource?.catalogNum || navSource?.CatalogNum || ''],
        vendorCatalogNum:           [popNav?.vendorProduct?.catalogNum || popNav?.vendorProduct?.CatalogNum || ''],
        vendorLotNum:               [lotNumNav?.vendorLotNum || lotNumNav?.VendorLotNum || popNav?.vendorLotNum || popNav?.VendorLotNum || ''],
        quantityAvailable:          [initialQuantityAvailable],
        unitPrice:                  [product.unitPrice || product.UnitPrice || 0],
        unitType:                   [product.unitType || product.UnitType || ''],
        quantity:                   [product.quantity || product.Quantity || 1],
        lineNumber:                 [product.lineNumber || product.LineNumber || 0],
        description:                [navSource?.description || navSource?.Description || ''],
        notes:                      [product.notes || product.Notes || ''],
        desiredPackaging:           [product.desiredPackaging || product.DesiredPackaging || ''],
        poNumber:                   [product.poNumber || product.PONumber || product.PoNumber || ''],
        costPerUnit:                [product.costPerUnit || product.CostPerUnit || 0]
      }));
      if (type === 'lotNum') {
        this.setupLotNumAutocomplete(this.products.length - 1);
      } else {
        this.setupPurchaseOrderProductAutocomplete(this.products.length - 1);
      }
    });
    this.enrichLineItems();
    if (!isCopy) {
      this.setSelectsDisabled(true);
    }
  }

  loadUnitTypes(): void {
    this.apiService.get<string[]>('product/unitTypes').subscribe({
      next: (data) => {
        this.unitTypes = data;
      },
      error: (err) => {
        console.error('Error loading unit types:', err);
        // Fallback to default values
        this.unitTypes = [];
      }
    });
  }

  loadLatestOrderNumber(): void {
    this.apiService.get<any>(`salesorder/latest-number`).subscribe({
      next: (data) => {
        this.salesOrderForm.patchValue({ number: data.orderNumber }, { emitEvent: false });
      },
      error: (err) => {
        console.error('Error loading latest order number:', err);
        this.salesOrderForm.patchValue({ number: '' }, { emitEvent: false });
      }
    });
  }

  loadOrderStatuses(): void {
    this.apiService.get<string[]>('misc/orderStatuses').subscribe({
      next: (data) => {
        this.orderStatuses = data;
      },
      error: (err) => {
        console.error('Error loading order statuses:', err);
        this.orderStatuses = [];
      }
    });
  }

  loadPaymentTerms(): void {
    this.apiService.get<string[]>('misc/paymentTerms').subscribe({
      next: (data) => {
        this.paymentTermsList = data || [];
        if (!this.salesOrderId && !this.salesOrderForm.get('paymentTerms')?.value) {
          const defaultTerm = this.paymentTermsList.find((t: any) => (t.name || t.Name) === 'Net30');
          if (defaultTerm) {
            this.salesOrderForm.patchValue({ paymentTerms: defaultTerm.name || defaultTerm.Name }, { emitEvent: false });
          }
        }
      },
      error: (err) => {
        console.error('Error loading payment terms:', err);
        this.paymentTermsList = [];
      }
    });
  }

  loadCustomers(): void {
    this.apiService.get<any[]>('customer/all').subscribe({
      next: (data) => {
        this.customers = data || [];
      },
      error: (err) => {
        console.error('Error loading customers:', err);
        this.customers = [];
      }
    });
  }

  private _filterCustomers(value: string): any[] {
    if (typeof value !== 'string') return this.customers;
    const filterValue = value.toLowerCase();
    return this.customers.filter(customer => {
      const name = customer.name || customer.Name || '';
      const id = (customer.id || customer.Id || '').toString();
      return name.toLowerCase().includes(filterValue) || id.includes(filterValue);
    });
  }

  displayCustomer(customer: any): string {
    return customer ? `${customer.name || customer.Name || ''} (ID: ${customer.id || customer.Id || ''})` : '';
  }

  onCustomerSelected(customer: any): void {
    if (customer) {
      const customerId = customer.id || customer.Id;
      this.salesOrderForm.patchValue({ customerId: customerId });
    }
  }

  clearCustomer(): void {
    this.salesOrderForm.patchValue({
      customerSearch: '',
      customerId: null,
      customerContactId: null,
      billToAddressId: null,
      shipToAddressId: null
    });
    this.customerContacts = [];
    this.customerAddresses = [];
  }

  loadCustomerContacts(customerId: string | number): void {
    this.apiService.get<any[]>(`contact/customer/all/${customerId}`).subscribe({
      next: (data) => {
        this.customerContacts = data || [];
        // Manually trigger change detection to update the mat-select dropdown
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading customer contacts:', err);
        this.customerContacts = [];
      }
    });
  }

  loadCustomerAddresses(customerId: string | number): void {
    this.apiService.get<any[]>(`address/customer/all/${customerId}`).subscribe({
      next: (data) => {
        this.customerAddresses = data || [];
        // Manually trigger change detection to update the mat-select dropdowns
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading customer addresses:', err);
        this.customerAddresses = [];
      }
    });
  }

  loadPurchaseOrderProducts(): void {
    this.apiService.get<any[]>('purchaseorder/pending-inventory').subscribe({
      next: (data) => {
        this.purchaseOrderProductsList = data || [];
        this.enrichLineItems();
      },
      error: (err) => {
        console.error('Error loading purchase order products:', err);
        this.purchaseOrderProductsList = [];
      }
    });
  }

  loadLotNums(): void {
    this.apiService.get<any[]>('product/lotnums/all').subscribe({
      next: (data) => {
        this.lotNumsList = data || [];
        this.enrichLineItems();
      },
      error: (err) => {
        console.error('Error loading lot numbers:', err);
        this.lotNumsList = [];
      }
    });
  }

  private enrichLineItems(): void {
    this.products.controls.forEach((control) => {
      const type = control.get('lineItemType')?.value;
      if (type === 'lotNum') {
        const lotNumId = control.get('lotNumId')?.value;
        if (lotNumId && this.lotNumsList.length) {
          const found = this.lotNumsList.find(ln => (ln.id || ln.Id) == lotNumId);
          if (found) {
            control.patchValue({
              lotNumSearch:      found,
              productId:         found.vendorProduct?.product?.id || found.product?.id || found.product?.Id || '',
              productName:       found.vendorProduct?.product?.name || found.vendorProduct?.product?.Name || found.product?.name || found.product?.Name || '',
              catalogNum:        found.vendorProduct?.product?.catalogNum || found.vendorProduct?.product?.CatalogNum || found.product?.catalogNum || found.product?.CatalogNum || '',
              vendorCatalogNum:  found.vendorProduct?.catalogNum || found.VendorProduct?.CatalogNum || control.get('vendorCatalogNum')?.value || '',
              vendorLotNum:      found.vendorLotNum || found.VendorLotNum || control.get('vendorLotNum')?.value || '',
              unitType:          found.unitType || found.UnitType || control.get('unitType')?.value || '',
              quantityAvailable: found.quantityAvailable ?? found.QuantityAvailable ?? null,
              description:       found.vendorProduct?.product?.description || found.vendorProduct?.product?.Description || found.product?.description || found.product?.Description || control.get('description')?.value || ''
            }, { emitEvent: false });
          }
        }
      } else {
        const popId = control.get('purchaseOrderProductId')?.value;
        if (popId && this.purchaseOrderProductsList.length) {
          const found = this.purchaseOrderProductsList.find(pop => (pop.id || pop.Id) == popId);
          if (found) {
            const qty = found.quantity || found.Quantity || 0;
            const qtyReceived = found.quantityReceived || found.QuantityReceived || 0;
            control.patchValue({
              purchaseOrderProductSearch: found,
              productId:         found.vendorProduct?.product?.id || found.product?.id || found.product?.Id || '',
              productName:       found.vendorProduct?.product?.name || found.vendorProduct?.product?.Name || found.product?.name || found.product?.Name || '',
              catalogNum:        found.vendorProduct?.product?.catalogNum || found.vendorProduct?.product?.CatalogNum || found.product?.catalogNum || found.product?.CatalogNum || '',
              vendorCatalogNum:  found.vendorProduct?.catalogNum || found.VendorProduct?.CatalogNum || control.get('vendorCatalogNum')?.value || '',
              vendorLotNum:      found.lotNum?.vendorLotNum || found.LotNum?.VendorLotNum || control.get('vendorLotNum')?.value || '',
              unitType:          found.unitType || found.UnitType || control.get('unitType')?.value || '',
              quantityAvailable: found.quantityAvailable ?? found.QuantityAvailable ?? 0,
              description:       found.vendorProduct?.product?.description || found.vendorProduct?.product?.Description || found.product?.description || found.product?.Description || control.get('description')?.value || ''
            }, { emitEvent: false });
          }
        }
      }
    });
  }

  setupPurchaseOrderProductAutocomplete(index: number): void {
    const control = this.products.at(index).get('purchaseOrderProductSearch')!;
    this.filteredPurchaseOrderProductsList[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map(value => this._filterPurchaseOrderProducts(value || ''))
    );
  }

  setupLotNumAutocomplete(index: number): void {
    const control = this.products.at(index).get('lotNumSearch')!;
    this.filteredLotNumsList[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map(value => this._filterLotNums(value || ''))
    );
  }

  _filterPurchaseOrderProducts(value: string | any): any[] {
    if (typeof value !== 'string') return this.purchaseOrderProductsList;
    const f = value.toLowerCase();
    return this.purchaseOrderProductsList.filter(pop => {
      const poNum   = (pop.purchaseOrder?.number || pop.purchaseOrder?.Number || '').toLowerCase();
      const catalog = (pop.vendorProduct?.product?.catalogNum || pop.vendorProduct?.product?.CatalogNum || pop.product?.catalogNum || pop.product?.CatalogNum || '').toLowerCase();
      const name    = (pop.vendorProduct?.product?.name || pop.vendorProduct?.product?.Name || pop.product?.name || pop.product?.Name || '').toLowerCase();
      return poNum.includes(f) || catalog.includes(f) || name.includes(f);
    });
  }

  _filterLotNums(value: string | any): any[] {
    if (typeof value !== 'string') return this.lotNumsList;
    const f = value.toLowerCase();
    return this.lotNumsList.filter(ln => {
      const num        = (ln.number  || ln.Number  || '').toLowerCase();
      const catalog    = (ln.vendorProduct?.product?.catalogNum || ln.vendorProduct?.product?.CatalogNum || ln.product?.catalogNum || ln.product?.CatalogNum || '').toLowerCase();
      const name       = (ln.vendorProduct?.product?.name || ln.vendorProduct?.product?.Name || ln.product?.name || ln.product?.Name || '').toLowerCase();
      const vendorName = (ln.vendorProduct?.vendor?.name || ln.vendorProduct?.vendor?.Name || '').toLowerCase();
      return num.includes(f) || catalog.includes(f) || name.includes(f) || vendorName.includes(f);
    });
  }

  displayPurchaseOrderProduct(pop: any): string {
    if (!pop) return '';
    const poNum = pop.purchaseOrder?.number || pop.purchaseOrder?.Number || '';
    const name  = pop.vendorProduct?.product?.name || pop.vendorProduct?.product?.Name || pop.product?.name || pop.product?.Name || '';
    return poNum ? `${poNum} — ${name}` : name;
  }

  displayLotNum(lotNum: any): string {
    return lotNum ? (lotNum.number || lotNum.Number || '') : '';
  }

  onPurchaseOrderProductSelected(index: number, pop: any): void {
    const purchaseOrderProductId = pop.id || pop.Id;
    const productId    = pop.vendorProduct?.product?.id || pop.product?.id || pop.product?.Id || '';
    const productName  = pop.vendorProduct?.product?.name || pop.vendorProduct?.product?.Name || pop.product?.name || pop.product?.Name || '';
    const catalogNum   = pop.vendorProduct?.product?.catalogNum || pop.vendorProduct?.product?.CatalogNum || pop.product?.catalogNum || pop.product?.CatalogNum || '';
    const unitType     = pop.unitType || pop.UnitType || '';
    const unitPrice    = pop.unitPrice || pop.UnitPrice || 0;
    const description  = pop.vendorProduct?.product?.description || pop.vendorProduct?.product?.Description || pop.product?.description || pop.product?.Description || '';
    const qty               = pop.quantity || pop.Quantity || 0;
    const qtyReceived       = pop.quantityReceived || pop.QuantityReceived || 0;
    const quantityAvailable = pop.quantityAvailable ?? pop.QuantityAvailable ?? 0;
    const poNumber          = pop.purchaseOrder?.number || pop.purchaseOrder?.Number || pop.PurchaseOrder?.Number || '';
    const costPerUnit       = pop.unitPrice || pop.UnitPrice || 0;
    const desiredPackaging  = pop.desiredPackaging || pop.DesiredPackaging || '';
    const vendorLotNum = pop.lotNum?.vendorLotNum || pop.LotNum?.VendorLotNum || '';
    const vendorCatalogNum = pop.vendorProduct?.catalogNum || pop.vendorProduct?.CatalogNum || '';
    this.products.at(index).patchValue({ purchaseOrderProductId, productId, productName, catalogNum, vendorLotNum, vendorCatalogNum, unitType, unitPrice, description, quantityAvailable, poNumber, costPerUnit, desiredPackaging });
  }

  onLotNumSelected(index: number, lotNum: any): void {
    const lotNumId          = lotNum.id || lotNum.Id;
    const productId         = lotNum.vendorProduct?.product?.id || lotNum.product?.id || lotNum.product?.Id || '';
    const catalogNum        = lotNum.vendorProduct?.product?.catalogNum || lotNum.vendorProduct?.product?.CatalogNum || lotNum.product?.catalogNum || lotNum.product?.CatalogNum || '';
    const productName       = lotNum.vendorProduct?.product?.name || lotNum.vendorProduct?.product?.Name || lotNum.product?.name || lotNum.product?.Name || '';
    const unitType          = lotNum.unitType || lotNum.UnitType || '';
    const quantityAvailable = lotNum.quantityAvailable ?? lotNum.QuantityAvailable ?? null;
    const description       = lotNum.vendorProduct?.product?.description || lotNum.vendorProduct?.product?.Description || lotNum.product?.description || lotNum.product?.Description || '';
    const vendorLotNum = lotNum.vendorLotNum || lotNum.VendorLotNum || '';
    const vendorCatalogNum = lotNum.vendorProduct?.catalogNum || lotNum.vendorProduct?.CatalogNum || '';
    this.products.at(index).patchValue({ lotNumId, productId, catalogNum, productName, unitType, quantityAvailable, description, vendorLotNum, vendorCatalogNum });
  }

  clearPurchaseOrderProduct(index: number): void {
    this.products.at(index).patchValue({
      purchaseOrderProductSearch: '', purchaseOrderProductId: '', productId: '', productName: '',
      catalogNum: '', quantityAvailable: null, description: '', vendorLotNum: '', vendorCatalogNum: ''

    });
  }

  clearLotNum(index: number): void {
    this.products.at(index).patchValue({
      lotNumSearch: '', lotNumId: '', productId: '', productName: '',
      catalogNum: '', quantityAvailable: null, description: '',
      vendorLotNum: '', vendorCatalogNum: ''
    });
  }

  // Getter for products FormArray
  get products(): FormArray {
    return this.salesOrderForm.get('products') as FormArray;
  }

  // Get descriptive title for a product line item
  getProductTitle(index: number): string {
    const item = this.products.at(index).value;
    const type = item.lineItemType;
    const quantity = item.quantity || 0;
    const unitType = item.unitType || 'each';
    const unitPrice = item.unitPrice || 0;
    const total = this.calculateLineTotal(index);
    const catalogNum = item.catalogNum || '';
    const productName = item.productName || '';

    let identifier = '';
    if (type === 'lotNum') {
      const search = item.lotNumSearch;
      identifier = search
        ? (typeof search === 'object' ? (search.number || search.Number || '') : String(search))
        : (item.lotNumId ? `Lot ${item.lotNumId}` : '');
    } else {
      const search = item.purchaseOrderProductSearch;
      identifier = search
        ? (typeof search === 'object' ? this.displayPurchaseOrderProduct(search) : String(search))
        : (item.purchaseOrderProductId ? `PO Product ${item.purchaseOrderProductId}` : '');
    }

    const label = identifier
      ? (productName && !identifier.includes(productName) ? `${identifier} — ${productName}` : identifier)
      : catalogNum;

    if (label && quantity) {
      return `${label} - ${quantity} ${unitType} @ $${unitPrice} = $${total.toFixed(2)}`;
    } else if (label) {
      return label;
    }
    return `Product Line ${index + 1}`;
  }

  // Create a new product line item FormGroup
  createProductLineItem(type: 'purchaseOrderProduct' | 'lotNum' = 'purchaseOrderProduct'): FormGroup {
    return this.fb.group({
      lineItemType:               [type],
      id:                         [null],
      purchaseOrderProductId:     [''],
      purchaseOrderProductSearch: [''],
      lotNumId:                   [''],
      lotNumSearch:               [''],
      productId:                  [''],
      productName:                [''],
      catalogNum:                 [''],
      vendorCatalogNum:           [''],
      vendorLotNum:               [''],
      quantityAvailable:          [0],
      unitPrice:                  [0, [Validators.required, Validators.min(0)]],
      unitType:                   [''],
      quantity:                   [1, [Validators.required, Validators.min(0.01)]],
      lineNumber:                 [this.products.length + 1],
      description:                [''],
      notes:                      [''],
      desiredPackaging:           [''],
      poNumber:                   [''],
      costPerUnit:                [0]    });
  }

  // Add a PO product line item
  addPurchaseOrderProduct(): void {
    this.products.push(this.createProductLineItem('purchaseOrderProduct'));
    this.setupPurchaseOrderProductAutocomplete(this.products.length - 1);
  }

  // Add a lot number line item
  addLotNumProduct(): void {
    this.products.push(this.createProductLineItem('lotNum'));
    this.setupLotNumAutocomplete(this.products.length - 1);
  }

  // Remove a product line item
  removeProduct(index: number): void {
    this.products.removeAt(index);
    // Update line numbers after removal
    this.updateLineNumbers();
  }

  // Update line numbers for all products
  updateLineNumbers(): void {
    this.products.controls.forEach((control, index) => {
      control.get('lineNumber')?.setValue(index + 1);
    });
  }

  // Calculate line total for a product
  calculateLineTotal(index: number): number {
    const product = this.products.at(index);
    const unitPrice = product.get('unitPrice')?.value || 0;
    const quantity = product.get('quantity')?.value || 0;
    return unitPrice * quantity;
  }

  // Check whether the entered quantity exceeds the available quantity for a product line item
  isQuantityExceeded(index: number): boolean {
    const product = this.products.at(index);
    const quantityAvailable = product.get('quantityAvailable')?.value;
    if (quantityAvailable === null || quantityAvailable === undefined) return false;
    const quantity = product.get('quantity')?.value || 0;
    return quantity > quantityAvailable;
  }

  // Calculate total for all products
  // Calculate total for line items only (before fees and taxes)
  getLineItemsTotal(): number {
    let total = 0;
    for (let i = 0; i < this.products.length; i++) {
      total += this.calculateLineTotal(i);
    }
    return total;
  }

  calculateTotal(): number {
    let total = 0;
    for (let i = 0; i < this.products.length; i++) {
      total += this.calculateLineTotal(i);
    }
    // Add fees and taxes
    const taxAmount = this.salesOrderForm.get('taxAmount')?.value || 0;
    const shipFee = this.salesOrderForm.get('shipFee')?.value || 0;
    const handlingFee = this.salesOrderForm.get('handlingFee')?.value || 0;
    const discountAmount = this.salesOrderForm.get('discountAmount')?.value || 0;
    return total + taxAmount + shipFee + handlingFee - discountAmount;
  }

  onSubmit() {
    if (this.salesOrderForm.valid) {
      const formValue = this.salesOrderForm.value;
      
      // Prepare data for API (convert to PascalCase for C# backend)
      var salesOrderData: any = {
        PurchaseOrders: this.purchaseOrders.map((po: any) => ({
          ...(po.id ? { Id: po.id } : {}),
        })),
        Number: formValue.number,
        Date: toUtcIsoDate(formValue.date),
        Status: formValue.status,
        Shipper: formValue.shipper,
        ShipAccountNum: formValue.shipAccountNum,
        ShipTerms: formValue.shipTerms,
        PaymentTerms: formValue.paymentTerms,
        DesiredShipDate: toUtcIsoDate(formValue.desiredShipDate),
        ShipAttentionTo: formValue.shipAttentionTo,
        SalesPerson: formValue.salesPerson,
        SubTotal: this.getLineItemsTotal(),
        TaxAmount: formValue.taxAmount,
        ShippingAmount: formValue.shipFee,
        HandlingFee: formValue.handlingFee,
        DiscountAmount: formValue.discountAmount,
        TotalAmount: this.calculateTotal(),
        CustomerId: formValue.customerId,
        CustomerPO: formValue.customerPO,
        BillToAddressId: formValue.billToAddressId,
        ShipToAddressId: formValue.shipToAddressId,
        CustomerContactId: formValue.customerContactId,
        Products: formValue.products.map((p: any) => ({
          ...(p.id ? { Id: p.id } : {}),
          ...(p.lineItemType === 'purchaseOrderProduct'
            ? { PurchaseOrderProductId: p.purchaseOrderProductId || null }
            : { LotNumId: p.lotNumId || null }),
          UnitPrice: p.unitPrice,
          UnitType: p.unitType || null,
          Quantity: p.quantity,
          LineNumber: p.lineNumber,
          DiscountPercent: p.discountPercent || null,
          DiscountAmount: p.discountAmount || null,
          TaxAmount: p.taxAmount || null,
          Notes: p.notes,
          DesiredPackaging: p.desiredPackaging || '',
          PONumber: p.poNumber || '',
          CostPerUnit: p.costPerUnit || 0
        }))
      };
      
      this.errorMessage = '';
      if (this.salesOrderId) {
        salesOrderData.Id = this.salesOrderId;
        this.apiService.put<any>(`salesorder/${this.salesOrderId}`, salesOrderData).subscribe({
          next: () => this.router.navigate(['/orders']),
          error: (err) => {
            console.error('Error updating sales order:', err);
            this.showError('Failed to save sales order. Please try again.', err);
          }
        });
      } else {
        this.apiService.post<any>('salesorder', salesOrderData).subscribe({
          next: () => this.router.navigate(['/orders']),
          error: (err) => {
            console.error('Error creating sales order:', err);
            this.showError('Failed to create sales order. Please try again.', err);
          }
        });
      }
    }
  }

  onCancel() {
    if (this.salesOrderId) {
      this.isEditMode = false;
      this.loadSalesOrder(this.salesOrderId);
    } else {
      this.router.navigate(['/orders']);
    }
  }

  downloadPdf(): void {
    this.apiService.getBlob(`salesorder/${this.salesOrderId}/pdf`).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const number = this.salesOrderForm.get('number')?.value || this.salesOrderId;
        a.download = `${number}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading PDF:', err);
        this.showError('Failed to generate PDF. Please try again.', err);
      }
    });
  }

  downloadLabels(): void {
    const formValue = this.salesOrderForm.getRawValue();
    const missingLotCount = (formValue.products || []).filter((p: any) => !p.lotNumId).length;
    if (missingLotCount > 0) {
      const proceed = confirm(
        `Not all products are assigned a lot number. ${missingLotCount} product line${missingLotCount > 1 ? 's' : ''} ` +
        `will have 'NA' for lot number, concentration, etc. on the generated labels. Continue?`);
      if (!proceed) {
        return;
      }
    }

    this.apiService.getBlob(`salesorder/${this.salesOrderId}/labels`).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const number = this.salesOrderForm.get('number')?.value || this.salesOrderId;
        a.download = `${number}-labels.docx`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading labels:', err);
        this.showError('Failed to generate labels. Please try again.', err);
      }
    });
  }

  createSalesInvoice(): void {
    const formValue = this.salesOrderForm.getRawValue();

    const hasPendingInventory = (formValue.products || []).some((p: any) => !p.lotNumId);
    if (hasPendingInventory) {
      this.pendingInventoryError = true;
      this.cdr.detectChanges();
      return;
    }
    this.pendingInventoryError = false;

    // An existing proforma is normally converted into its invoice rather than having a
    // separate one created alongside it, which would double up the line items.
    if (this.proformas.length > 0) {
      const count = this.proformas.length;
      const proceed = confirm(
        `This sales order already has ${count} related proforma${count > 1 ? 's' : ''}. ` +
        `Proformas are normally converted into an invoice rather than creating a separate one. ` +
        `Create a new sales invoice anyway?`);
      if (!proceed) {
        return;
      }
    }

    const prefill = {
      customerId: formValue.customerId,
      customerSearch: formValue.customerSearch,
      customerContactId: formValue.customerContactId,
      billingAddressId: formValue.billToAddressId,
      shippingAddressId: formValue.shipToAddressId,
      customerPO: formValue.customerPO,
      shipAttentionTo: formValue.shipAttentionTo,
      salesPerson: formValue.salesPerson,
      paymentTerms: formValue.paymentTerms,
      discountAmount: formValue.discountAmount,
      taxAmount: formValue.taxAmount,
      shippingAmount: formValue.shipFee,
      handlingFee: formValue.handlingFee,

      purchaseOrders: this.purchaseOrders.map((po: any) => ({
        id: po.id,
        number: po.number,
        date: po.date,
        status: po.status,
        totalAmount: po.totalAmount
      })),
      salesOrder: {id: this.salesOrderId, number: formValue.number, date: formValue.date, status: formValue.status, 
        totalAmount: this.calculateTotal()},
      products: formValue.products.map((p: any) => ({
        lotNumId: p.lotNumId,
        //purchaseOrderProductId: p.purchaseOrderProductId,
        //productId: p.productId,
        //productName: p.productName,
        quantity: p.quantity,
        unitPrice: p.unitPrice,
        unitType: p.unitType,
        description: p.description,
        desiredPackaging: p.desiredPackaging,
      }))
    };
    this.router.navigate(['/sales-invoices/new'], { state: { prefill } });
  }

  createSalesProforma(): void {
    const formValue = this.salesOrderForm.getRawValue();

    const prefill = {
      customerId: formValue.customerId,
      customerSearch: formValue.customerSearch,
      customerContactId: formValue.customerContactId,
      billingAddressId: formValue.billToAddressId,
      shippingAddressId: formValue.shipToAddressId,
      customerPO: formValue.customerPO,
      shipAttentionTo: formValue.shipAttentionTo,
      salesPerson: formValue.salesPerson,
      paymentTerms: formValue.paymentTerms,
      discountAmount: formValue.discountAmount,
      taxAmount: formValue.taxAmount,
      shippingAmount: formValue.shipFee,
      handlingFee: formValue.handlingFee,
      salesOrderId: this.salesOrderId,

      purchaseOrders: this.purchaseOrders.map((po: any) => ({
        id: po.id,
        number: po.number,
        date: po.date,
        status: po.status,
        totalAmount: po.totalAmount
      })),
      salesOrder: { id: this.salesOrderId, number: formValue.number, date: formValue.date, status: formValue.status,
        totalAmount: this.calculateTotal() },
      // Proformas allow pending inventory (purchaseOrderProductId without a lotNumId assigned yet)
      products: formValue.products.map((p: any) => ({
        lotNumId: p.lotNumId,
        purchaseOrderProductId: p.purchaseOrderProductId,
        poNumber: p.poNumber,
        productId: p.productId,
        quantity: p.quantity,
        unitPrice: p.unitPrice,
        unitType: p.unitType,
        description: p.description,
        desiredPackaging: p.desiredPackaging,
      }))
    };
    this.router.navigate(['/proformas/new'], { state: { prefill } });
  }

  loadRelatedPurchaseOrders(id: number): void {
    this.apiService.get<any[]>(`salesorder/${id}/purchaseorders`).subscribe({
      next: (data) => {
        const raw: any[] = data || [];
        this.purchaseOrders = raw.map((po: any) => ({
          id: po.id || po.Id,
          number: po.number || po.Number || '',
          date: po.date || po.Date ? new Date(po.date || po.Date) : null,
          status: po.status || po.Status || '',
          totalAmount: po.totalAmount || po.TotalAmount || 0
        }));
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading related purchase orders:', err)
    });
  }

  viewPurchaseOrder(id: number): void {
    this.router.navigate(['/purchase-orders', id]);
  }

  removePurchaseOrder(id: number): void {
    this.apiService.delete<any>(`salesorder/${this.salesOrderId}/unlink-purchaseorder/${id}`).subscribe({
      next: () => this.loadRelatedPurchaseOrders(this.salesOrderId!),
      error: (err) => {
        console.error('Error unlinking purchase order:', err);
        this.showError('Failed to unlink purchase order. Please try again.', err);
      }
    });
  }

  openLinkPODialog(): void {
    const ref = this.dialog.open(OrderLinkingDialogComponent, {
      data: {
        orderLabel: 'Purchase Order',
        fetchUrl: 'purchaseorder/all',
        linkUrlPrefix: `salesorder/${this.salesOrderId}/link-purchaseorder`,
        alreadyLinkedIds: this.purchaseOrders.map(po => po.id)
      },
      width: '500px'
    });
    ref.afterClosed().subscribe(result => {
      if (result?.linkedOrder) {
        this.loadRelatedPurchaseOrders(this.salesOrderId!);
      }
    });
  }

  loadRelatedSalesInvoices(id: number): void {
    this.apiService.get<any[]>(`salesorder/${id}/invoices`).subscribe({
      next: (data) => {
        const raw: any[] = data || [];
        this.salesInvoices = raw.map((si: any) => ({
          id: si.id || si.Id,
          invoiceNumber: si.invoiceNumber || si.InvoiceNumber || si.number || si.Number || '',
          date: si.invoiceDate || si.InvoiceDate || si.date || si.Date ? new Date(si.invoiceDate || si.InvoiceDate || si.date || si.Date) : null,
          status: si.status || si.Status || '',
          totalAmount: si.totalAmount || si.TotalAmount || 0
        }));
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading related sales invoices:', err)
    });
  }

  viewSalesInvoice(id: number): void {
    this.router.navigate(['/sales-invoices', id]);
  }

  loadRelatedProformas(id: number): void {
    this.apiService.get<any[]>(`proforma/salesorder/${id}`).subscribe({
      next: (data) => {
        const raw: any[] = data || [];
        this.proformas = raw.map((pf: any) => ({
          id: pf.id || pf.Id,
          proformaNumber: pf.number || pf.Number || '',
          date: pf.date || pf.Date ? new Date(pf.date || pf.Date) : null,
          status: pf.status || pf.Status || '',
          totalAmount: pf.totalAmount || pf.TotalAmount || 0
        }));
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading related proformas:', err)
    });
  }

  viewProforma(id: number): void {
    this.router.navigate(['/proformas', id]);
  }

  removeSalesInvoice(id: number): void {
    this.apiService.delete<any>(`salesorder/${this.salesOrderId}/unlink-invoice/${id}`).subscribe({
      next: () => this.loadRelatedSalesInvoices(this.salesOrderId!),
      error: (err) => {
        console.error('Error unlinking sales invoice:', err);
        this.showError('Failed to unlink sales invoice. Please try again.', err);
      }
    });
  }

  openLinkInvoiceDialog(): void {
    const ref = this.dialog.open(OrderLinkingDialogComponent, {
      data: {
        orderLabel: 'Sales Invoice',
        fetchUrl: 'invoice/all',
        linkUrlPrefix: `salesorder/${this.salesOrderId}/link-invoice`,
        alreadyLinkedIds: this.salesInvoices.map(si => si.id)
      },
      width: '500px'
    });
    ref.afterClosed().subscribe(result => {
      if (result?.linkedOrder) {
        this.loadRelatedSalesInvoices(this.salesOrderId!);
      }
    });
  }

  formatAddress(address: any): string {
    const parts = [
      address.addressLine1 || address.AddressLine1,
      address.addressLine2 || address.AddressLine2,
      address.addressLine3 || address.AddressLine3,
      address.addressLine4 || address.AddressLine4,
      address.addressLine5 || address.AddressLine5,
      address.city || address.City,
      address.state || address.State,
      address.postalCode || address.PostalCode,
      address.country || address.Country
    ].filter(Boolean);
    return parts.join(', ') || '-';
  }

  getAddressTooltip(id: any, addresses: any[]): string {
    if (!id) return '';
    const found = addresses.find(a => (a.id || a.Id) == id);
    return found ? this.formatAddress(found) : '';
  }

  getContactTooltip(id: any): string {
    if (!id) return '';
    const found = this.customerContacts.find(c => (c.id || c.Id) == id);
    if (!found) return '';
    const parts: string[] = [];
    const name = `${found.firstName || found.FirstName || ''} ${found.lastName || found.LastName || ''}`.trim();
    if (name) parts.push(name);
    const email = found.email || found.Email || '';
    if (email) parts.push(email);
    const phone = found.phone || found.Phone || '';
    if (phone) parts.push(phone);
    return parts.join('\n');
  }

  getLotNumLabel(id: any): string {
    if (!id) return '';
    const found = this.lotNumsList.find(ln => (ln.id || ln.Id) == id);
    return found ? (found.number || found.Number || '') : '';
  }
}
